"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/format";

type MonthOption = {
  key: string;
  year: number;
  month: number;
  label: string;
  count: number;
};

type Props = {
  months: MonthOption[];
  activeKey: string;
  onChange: (key: string) => void;
  className?: string;
};

const MONTH_NAME = new Intl.DateTimeFormat("en-US", { month: "short" });

/** Compact, smoothly scrollable month rail for the currently selected year. */
export function MonthYearFilter({
  months,
  activeKey,
  onChange,
  className,
}: Props) {
  const active = months.find((month) => month.key === activeKey) ?? months[0];
  const monthsForYear = useMemo(
    () => months.filter((month) => month.year === active?.year),
    [active?.year, months],
  );
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({
    overflow: false,
    left: false,
    right: false,
  });

  const updateScrollState = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    setScrollState({
      overflow: maxScroll > 2,
      left: scroller.scrollLeft > 2,
      right: scroller.scrollLeft < maxScroll - 2,
    });
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [monthsForYear, updateScrollState]);

  // Keep the active month tab in view within the horizontal rail — scrolls
  // only the rail's own scrollLeft, never the page. `scrollIntoView` (even
  // with block:"nearest") can walk up to the document and vertically scroll
  // the whole page when the tab isn't inside an overflow-constrained
  // ancestor on that axis, which is what this component sits inside on the
  // package detail page. Also skips the very first mount so opening the
  // page doesn't jump-scroll straight to wherever this filter happens to
  // render.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    const scroller = scrollerRef.current;
    const activeTab = scroller?.querySelector<HTMLElement>(
      `[data-month-key="${activeKey}"]`,
    );
    if (!scroller || !activeTab) return;
    const tabLeft = activeTab.offsetLeft;
    const tabRight = tabLeft + activeTab.offsetWidth;
    const viewLeft = scroller.scrollLeft;
    const viewRight = viewLeft + scroller.clientWidth;
    if (tabLeft < viewLeft) {
      scroller.scrollTo({ left: tabLeft, behavior: "smooth" });
    } else if (tabRight > viewRight) {
      scroller.scrollTo({
        left: tabRight - scroller.clientWidth,
        behavior: "smooth",
      });
    }
  }, [activeKey]);

  if (!active) return null;

  const slide = (direction: -1 | 1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({
      left: direction * Math.max(120, scroller.clientWidth * 0.72),
      behavior: "smooth",
    });
  };

  return (
    <fieldset className={cn("min-w-0", className)}>
      <legend className="sr-only">Filter departures by month</legend>
      <div className="flex min-w-0 items-center gap-1">
        {scrollState.overflow ? (
          <button
            type="button"
            onClick={() => slide(-1)}
            disabled={!scrollState.left}
            aria-label="Show previous months"
            className="inline-flex h-8 w-7 shrink-0 items-center justify-center rounded-sm bg-paper text-muted transition-colors hover:bg-line/60 hover:text-headings focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-headings/15 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronLeft size={14} aria-hidden />
          </button>
        ) : null}

        <div
          ref={scrollerRef}
          onScroll={updateScrollState}
          className="flex min-w-0 flex-1 snap-x gap-2"
          role="tablist"
          aria-label={`Departure month for ${active.year}`}
        >
          {monthsForYear.map((month) => {
            const selected = month.key === active.key;
            return (
              <button
                key={month.key}
                data-month-key={month.key}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => onChange(month.key)}
                // className={cn(
                //   "relative flex min-h-9 min-w-[48px] shrink-0 snap-start items-center justify-center border-b-2 px-2 text-[10px] font-extrabold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-headings/15",
                //   selected
                //     ? "border-headings text-headings"
                //     : "border-transparent text-muted-soft hover:border-line hover:text-headings",
                // )}
                className={cn(
                  "shrink-0 font-extrabold border shadow-sm ease-in transition-all delay-75 duration-100 relative px-4 py-1.5 text-xs after:absolute after:block after:left-1/2 after:h-[10px] after:w-[10px] after:-translate-x-1/2 after:rotate-45 after:border-b after:border-r after:border-black after:bg-[#ACE6C0] after:transition-all after:duration-100",
                  selected
                    ? "bg-[#ACE6C0] text-headings shadow-sm border-black rounded after:opacity-100 after:bottom-[-6px]"
                    : "text-muted hover:text-headings bg-white rounded-full border-black/30 after:opacity-0 after:bottom-[-2px]",
                )}
              >
                {MONTH_NAME.format(new Date(month.year, month.month, 1))}
              </button>
            );
          })}
        </div>

        {scrollState.overflow ? (
          <button
            type="button"
            onClick={() => slide(1)}
            disabled={!scrollState.right}
            aria-label="Show next months"
            className="inline-flex h-8 w-7 shrink-0 items-center justify-center rounded-sm bg-paper text-muted transition-colors hover:bg-line/60 hover:text-headings focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-headings/15 disabled:cursor-not-allowed disabled:opacity-30"
          >
            <ChevronRight size={14} aria-hidden />
          </button>
        ) : null}
      </div>
    </fieldset>
  );
}

export type { MonthOption };
